import { GroupSecurity } from '@quromedical/fhir-common';
import type { PatientRouteParams } from 'app/routes';
import { Text } from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { Drawer } from 'expo-router/drawer';
import { useUserSession } from 'hooks';
import { useCommunicationAlerts } from 'hooks/useCommunicationAlerts';
import type { DrawerType } from 'hooks/useDrawerType';
import { useFiniteState } from 'hooks/useFiniteState';
import { DrawerNavigatorContent } from 'navigation/DrawerNavigatorContent';
import {
  Badge,
  hiddenDrawerItemStyle,
  useCommonDrawerProps,
  Wrapper,
} from 'navigation/drawerStyles';
import { PatientVitalsProvider } from 'providers/PatientVitalsProvider';
import { strings } from 'strings';

export default function Layout() {
  const screenOptions = useCommonDrawerProps();
  const drawerType = useFiniteState<DrawerType>(screenOptions.drawerType);

  const { patientId } = useLocalSearchParams<PatientRouteParams>();
  const { data } = useCommunicationAlerts(patientId);

  const session = useUserSession();

  const hasClinicalPermission = session.hasAny([
    GroupSecurity.MedicationStatementRead,
  ]);
  const clinicalDrawerItemStyle = hasClinicalPermission
    ? undefined
    : hiddenDrawerItemStyle;

  const hasSearchPermission = session.hasAny([
    GroupSecurity.FeaturePreviewAISearch,
    GroupSecurity.FeaturePreviewAI,
  ]);

  const searchDrawerItemStyle = hasSearchPermission
    ? undefined
    : hiddenDrawerItemStyle;

  const hasTaskPermission = session.hasAny([GroupSecurity.TaskRead]);
  const tasksDrawerItemStyle = hasTaskPermission
    ? undefined
    : hiddenDrawerItemStyle;

  return (
    <PatientVitalsProvider>
      <Drawer
        screenOptions={{
          ...screenOptions,
          drawerType: drawerType.value,
          headerShown: drawerType.value !== 'permanent',
        }}
        drawerContent={(props) => <DrawerNavigatorContent {...props} />}
      >
        <Drawer.Screen name="profile" options={{ title: 'Profile' }} />
        <Drawer.Screen name="vitals" options={{ title: 'Vitals' }} />
        <Drawer.Screen
          name="clinical"
          options={{
            title: 'Clinical',
            drawerItemStyle: clinicalDrawerItemStyle,
          }}
        />
        <Drawer.Screen name="notes" options={{ title: 'Notes' }} />
        <Drawer.Screen
          name="tasks"
          options={{ title: 'Tasks', drawerItemStyle: tasksDrawerItemStyle }}
        />
        <Drawer.Screen name="medication" options={{ title: 'Medication' }} />
        <Drawer.Screen
          name="service-request"
          options={{ title: 'Pathology' }}
        />
        <Drawer.Screen name="media" options={{ title: 'Media' }} />
        <Drawer.Screen name="documents" options={{ title: 'Documents' }} />
        <Drawer.Screen
          name="communication"
          options={{
            title: 'Communication',
            drawerLabel: () => (
              <Wrapper>
                <Text>{strings.CommunicationTitle}</Text>
                {data && data.length !== 0 ? (
                  <Badge>
                    <Text>{data.length}</Text>
                  </Badge>
                ) : null}
              </Wrapper>
            ),
          }}
        />
        <Drawer.Screen
          name="search"
          options={{
            title: 'AI Search',
            drawerItemStyle: searchDrawerItemStyle,
          }}
        />
      </Drawer>
    </PatientVitalsProvider>
  );
}
